import { Col, Flex, Row } from 'antd';
import { DataList } from '@/components/ui/list/DataList';
import { Card, Tabs } from 'pink-lava-ui';
import { useUrlParamsContext } from '@/context/urlParamsContext';
import { useMemo, useCallback, type ReactNode } from 'react';
import BookingCard from '@/components/ui/BookingCard';
import { BoxIcon, ClockFadingIcon, DownloadIcon, LayoutGridIcon, WandSparklesIcon } from 'lucide-react';
import type { Booking, BookingCount } from '@dc-trans/data-layer';
import { useLocation, useNavigate } from 'react-router-dom';
import { triggerDownload } from '@/utils/download';
import { useNotification } from '@/context/notificationContext';
import { useBookingCount, useBookingList } from '@/features/booking/api/queries';
import { useDownloadData } from '@/mutations';

const BookingList = () => {
  const showNotification = useNotification();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { filters, updateUrlParams } = useUrlParamsContext();

  const { data: bookingCount } = useBookingCount();
  const downloadDataMutation = useDownloadData({
    meta: {
      message: 'Gagal download data, silahkan coba lagi.',
    },
  });

  // Use filters.status directly as activeTab to avoid circular dependency
  const activeTab = filters.status || 'all';

  // Handle tab changes by updating URL parameter
  const handleTabChange = useCallback(
    (newTab: string) => {
      if (newTab) {
        updateUrlParams({ status: newTab, page: 1 });
      }
    },
    [updateUrlParams]
  );

  const handleExport = () => {
    downloadDataMutation.mutate(
      {
        url: '/bookings/export',
        params: { ...filters, status: filters.status === 'all' ? undefined : filters.status },
      },
      {
        onSuccess: ({ data, filename }) => {
          triggerDownload(data, filename);
          showNotification.success({
            message: 'Data berhasil didownload.',
          });
        },
      }
    );
  };

  const mapBookingCount: {
    // eslint-disable-next-line no-unused-vars
    [K in keyof BookingCount]: { label: string; value: number | undefined; icon: ReactNode };
  } = useMemo(() => {
    return {
      all: {
        label: 'Semua',
        value: bookingCount?.data?.total,
        icon: <LayoutGridIcon size={20} />,
      },
      new: {
        label: 'Baru',
        value: bookingCount?.data?.new,
        icon: <BoxIcon size={20} />,
      },
      in_progress: {
        label: 'Sedang Berlangsung',
        value: bookingCount?.data?.in_progress,
        icon: <ClockFadingIcon size={20} />,
      },
      completed: {
        label: 'Selesai',
        value: bookingCount?.data?.completed,
        icon: <WandSparklesIcon size={20} />,
      },
    };
  }, [bookingCount]);

  return (
    <DataList
      title="Daftar Booking"
      filterFields={[
        {
          type: 'search',
          name: 'search',
          label: 'Cari Booking',
          searchOptions: [
            { name: 'tms_booking', label: 'No. Booking', placeholder: 'Cari No. Booking' },
            { name: 'do_number', label: 'No. DO', placeholder: 'Cari No. DO' },
            { name: 'ship_ref_number', label: 'No. Shipment', placeholder: 'Cari No. Shipment' },
          ],
          trigger: 'onChange',
        },
        {
          type: 'select',
          name: 'type',
          label: 'Tipe Booking',
          placeholder: 'Tipe Booking',
          rounded: 'large',
          options: [
            { value: 'instant', label: 'Instant' },
            { value: 'on_demand', label: 'On Demand' },
          ],
        },
        {
          type: 'search',
          name: 'origin_name',
          label: 'Asal Pengiriman',
          placeholder: 'Asal Pengiriman',
          trigger: 'onChange',
        },
        {
          type: 'search',
          name: 'dest_name',
          label: 'Tujuan Pengiriman',
          placeholder: 'Tujuan Pengiriman',
          trigger: 'onChange',
        },
      ]}
    >
      <Flex gap={16} className="w-full h-full" vertical>
        <Flex className="w-full flex-1 overflow-y-scroll" gap={16} vertical>
          <DataList.Header
            actions={{
              primary: {
                label: 'Export',
                onClick: handleExport,
                icon: <DownloadIcon size={16} />,
              },
            }}
            hideFilterLabels={['status']}
            colProps={{
              xs: 24,
              sm: 24,
              md: 12,
              lg: 8,
              xl: 6,
            }}
          >
            <Row gutter={[8, 8]} className="mt-2">
              {Object.entries(mapBookingCount).map(([key, data]) => (
                <Col xs={12} lg={6} key={key}>
                  <div className="border-gray-200 border-[1px] shadow-sm rounded-2xl px-3 py-1.5 items-center flex flex-row gap-2">
                    {data.icon}
                    <div className="flex text-sm font-medium flex-1 h-[40px] items-center">{data.label}</div>
                    <div className="text-xl font-bold">{typeof data.value === 'number' ? data.value : '-'}</div>
                  </div>
                </Col>
              ))}
            </Row>
          </DataList.Header>
          <Card
            rounded="large"
            classNames={{
              body: 'p-0! px-4!',
            }}
            className="mx-4!"
          >
            <Tabs
              activeKey={activeTab}
              onChange={handleTabChange}
              items={[
                {
                  key: 'all',
                  label: 'Semua',
                },
                {
                  key: 'new',
                  label: 'Baru',
                },
                {
                  key: 'in_progress',
                  label: 'Sedang Berlangsung',
                },
                {
                  key: 'completed',
                  label: 'Selesai',
                },
              ]}
            />
          </Card>
          <DataList.List<Booking>
            useQuery={useBookingList}
            renderItem={booking => (
              <BookingCard
                key={booking.ulid}
                booking={booking}
                onDetailClick={() =>
                  navigate(`/booking-list/${booking.ulid}`, {
                    state: {
                      previousPath: pathname,
                    },
                  })
                }
              />
            )}
          />
        </Flex>
      </Flex>
    </DataList>
  );
};

export default BookingList;
